import { ChangeEvent, forwardRef, useEffect, useId, useRef, useState } from 'react';
import classNames from 'classnames';
import { Icon } from '@components/icon';
import { AssistiveText } from '@components/input/AssistiveText';
import { Text } from '@components/text';
import './Checkbox.scss';

type OnChangeFunctionType = (event: ChangeEvent) => void;
type OnFocusFunctionType = (event: React.FocusEvent) => void;
type OnBlurFunctionType = (event: React.FocusEvent) => void;

export interface CheckboxProps {
  name?: string;
  id?: string;
  label?: string;
  disabled?: boolean;
  onChange?: OnChangeFunctionType;
  onFocus?: OnFocusFunctionType;
  onBlur?: OnBlurFunctionType;
  checked?: boolean;
  variant?: 'circle' | 'square';
  size?: 'small' | 'medium' | 'large';
  className?: string;
  children?: React.ReactNode;
  /** If true, applies error styling and sets aria-invalid on the input. */
  error?: boolean;
  /** Error or hint text rendered below the checkbox. When present, wired via
   *  aria-describedby so screen readers announce it on focus. */
  assistiveText?: string;
}

/**
 * Checkbox Base component, an <input type="checkbox" /> element is underneath to supports accessibility
 * @param name - Name attribute of the input element.
 * @param id - Used for accessibility to bind with a label.
 * @param disabled - If true, the component is disabled.
 * @param onChange - Callback fired when the state is changed.
 * @param onFocus - Callback fired when the state is focused.
 * @param onBlur - Callback fired when the state is blurred.
 * @param checked - If true, the component is checked.
 * @param variant - Square or Circle Variants.
 * @param size - Sets the size Small, Medium or Large.
 * @param className - Extend the styles applied to the component.
 * @param label - label text next to checkbox
 * @param children - Any React.ReactNode Element.
 * @param error - If true, applies error styling and sets aria-invalid on the input.
 * @param assistiveText - Error or hint text rendered below the checkbox.
 * @constructor
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      id,
      name,
      label,
      disabled,
      onChange,
      onFocus,
      onBlur,
      checked = false,
      variant = 'circle',
      size = 'medium',
      className,
      children,
      error,
      assistiveText,
      ...rest
    },
    ref,
  ) => {
    const generatedId = useId();
    const resolvedId = id ?? generatedId;
    const checkboxRef = useRef<HTMLInputElement | null>(null);
    const [isChecked, setIsChecked] = useState(checked);
    const [isFocused, setIsFocused] = useState(false);

    const focusHandler = (e: React.FocusEvent) => {
      setIsFocused(true);
      if (onFocus) onFocus(e);
    };

    const handleBlur = (e: React.FocusEvent) => {
      setIsFocused(false);
      if (onBlur) onBlur(e);
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      setIsChecked((prevState: boolean) => !prevState);
      if (onChange) onChange(e);
    };

    const handleClick = () => {
      if (disabled) return;
      checkboxRef?.current?.click();
    };

    // fixes Property 'current' does not exist on type '((instance: HTMLDivElement...' ts issue
    const initRefs = (element: HTMLInputElement) => {
      checkboxRef.current = element;
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    };

    useEffect(() => {
      setIsChecked(checked);
    }, [checked]);

    const assistiveTextId = assistiveText ? `${resolvedId}-text` : undefined;

    return (
      <div
        className={classNames(
          'vds-checkbox',
          {
            [`vds-checkbox--${size}`]: size,
            [`vds-checkbox--${variant}`]: variant,
            [`vds-checkbox--checked`]: isChecked,
            [`vds-checkbox--focused`]: isFocused,
            [`vds-checkbox--disabled`]: disabled,
            'vds-checkbox--error': error,
          },
          className,
        )}>
        <span className="vds-checkbox__icon-wrapper" onClick={handleClick}>
          <Icon
            className={classNames('vds-checkbox__icon', {
              'vds-checkbox__icon-circle': variant === 'circle',
            })}
            name={isChecked ? `checkbox-selected-${variant}` : `checkbox-unselected-${variant}`}
          />
        </span>

        <input
          id={resolvedId}
          ref={initRefs}
          type="checkbox"
          className="vds-checkbox__input"
          disabled={disabled}
          name={name}
          onChange={handleChange}
          onFocus={focusHandler}
          onBlur={handleBlur}
          checked={isChecked}
          aria-labelledby={`${resolvedId}-label`}
          aria-invalid={error || undefined}
          aria-describedby={assistiveTextId}
          {...rest}
        />
        <label htmlFor={resolvedId} id={`${resolvedId}-label`}>
          <Text variant="body-02" weight="regular" size="medium" color="secondary">
            {label}
            {children}
          </Text>
        </label>
        {assistiveText ? (
          <AssistiveText error={error} disabled={disabled} id={assistiveTextId}>
            {assistiveText}
          </AssistiveText>
        ) : null}
      </div>
    );
  },
);
